import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FileText, CheckCircle, Calendar, Users, Phone, ChevronDown, ChevronUp } from 'lucide-react';

const AdmissionsPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [steps, setSteps] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [phoneInfo, setPhoneInfo] = useState(null);
  const [heroImage, setHeroImage] = useState({
    image: 'https://images.unsplash.com/photo-1655141011386-1eaaa28aef6a',
    title: 'Kabul ve Kayıt',
    subtitle: 'Serpil Şahin Koleji ailesine katılmak için ilk adımı atın'
  });

  useEffect(() => {
    fetchFaqs();
    fetchSteps();
    fetchDocuments();
    fetchContactInfos();
    fetchHeroImage();
  }, []);

  const fetchHeroImage = async () => {
    try {
      const response = await fetch('http://localhost/api/settings.php?key=admissions_hero_image', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : {};
      
      if (data.setting_value) {
        try {
          const heroData = typeof data.setting_value === 'string' 
            ? JSON.parse(data.setting_value) 
            : data.setting_value;
          if (heroData.image) {
            setHeroImage(heroData);
          }
        } catch (e) {
          console.error('Error parsing hero image:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching hero image:', error);
    }
  };

  const getIcon = (iconName) => {
    const icons = {
      Phone: <Phone className="w-8 h-8" />,
      FileText: <FileText className="w-8 h-8" />,
      Users: <Users className="w-8 h-8" />,
      CheckCircle: <CheckCircle className="w-8 h-8" />,
      Calendar: <Calendar className="w-8 h-8" />,
    };
    return icons[iconName] || <Phone className="w-8 h-8" />;
  };

  const fetchSteps = async () => {
    try {
      const response = await fetch('http://localhost/api/admission-data.php?type=steps', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching steps:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setSteps(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching steps:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await fetch('http://localhost/api/admission-data.php?type=documents', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching documents:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      setDocuments(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchFaqs = async () => {
    try {
      const response = await fetch('http://localhost/api/faqs.php?active=1', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching FAQs:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      
      // Sadece aktif olanları al - kesinlikle sadece is_active = 1 veya '1' olanları
      const activeFaqs = data.filter(item => {
        const isActive = item.is_active;
        // Sadece kesinlikle 1 veya '1' olanları kabul et, başka hiçbir şeyi kabul etme
        return isActive === 1 || isActive === '1';
      });
      
      // Duplicate'leri filtrele (aynı soruya sahip olanları bir kez göster)
      const uniqueFaqs = [];
      const seenQuestions = new Set();
      
      for (const faq of activeFaqs) {
        const questionKey = faq.question?.toLowerCase().trim();
        if (questionKey && !seenQuestions.has(questionKey)) {
          seenQuestions.add(questionKey);
          uniqueFaqs.push(faq);
        }
      }
      
      setFaqs(uniqueFaqs);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const fetchContactInfos = async () => {
    try {
      const response = await fetch('http://localhost/api/pages.php?type=contact', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching contact infos:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      const phone = Array.isArray(data) ? data.find(info => info.type === 'phone') : null;
      setPhoneInfo(phone);
    } catch (error) {
      console.error('Error fetching contact infos:', error);
    }
  };


  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero Section */}
      <section className="relative h-64 mt-20" data-testid="admissions-hero-section">
        <div className="absolute inset-0">
          <img
            src={heroImage.image}
            alt={heroImage.title || 'Kabul ve Kayıt'}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-800/70" />
        </div>
        <div className="relative h-full flex items-center">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 fade-in-up">
              {heroImage.title || 'Kabul ve Kayıt'}
            </h1>
            <p className="text-lg text-gray-100 max-w-2xl fade-in-up" style={{ animationDelay: '0.2s' }}>
              {heroImage.subtitle || 'Serpil Şahin Koleji ailesine katılmak için ilk adımı atın'}
            </p>
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-20 bg-white" data-testid="process-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Başvuru <span className="gold-gradient-text">Süreci</span>
            </h2>
            <p className="text-lg text-gray-600">
              Daha basit adımlarla kayıt süreci
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.length > 0 ? (
              steps.map((step, index) => (
                <div key={step.id || index} data-testid={`step-${index}`} className="relative">
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all border-2 border-transparent hover:border-[#C0A062]">
                    <div className="w-16 h-16 rounded-full bg-[#C0A062] flex items-center justify-center text-white mb-4 mx-auto">
                      {getIcon(step.icon_name)}
                    </div>
                    <div className="text-center">
                      <div className="inline-block px-4 py-1 bg-red-900 text-white text-sm font-bold rounded-full mb-3">
                        Adım {index + 1}
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{step.title}</h3>
                      <p className="text-gray-600 text-sm">{step.description}</p>
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-12 -right-4 w-8 h-0.5 bg-[#C0A062]"></div>
                  )}
                </div>
              ))
            ) : (
              <div className="col-span-4 text-center py-12">
                <p className="text-gray-500">Başvuru süreci adımları yükleniyor...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Required Documents */}
      <section className="py-20 bg-gray-50" data-testid="documents-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Gerekli <span className="gold-gradient-text">Belgeler</span>
            </h2>
            <p className="text-gray-600">Kayıt işlemleri için aşağıdaki belgeleri hazırlayın</p>
          </div>

          <div className="bg-white rounded-2xl p-8 shadow-lg">
            {documents.length > 0 ? (
              <ul className="space-y-4">
                {documents.map((doc, index) => (
                  <li key={doc.id || index} data-testid={`document-${index}`} className="flex items-start">
                    <CheckCircle className="text-[#C0A062] mr-3 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{doc.name}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500 text-center py-8">Gerekli belgeler yükleniyor...</p>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-red-900/10" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Sıkça Sorulan <span className="gold-gradient-text">Sorular</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.length > 0 ? (
              faqs.map((faq, index) => (
                <div
                  key={faq.id || index}
                  data-testid={`faq-${index}`}
                  className="bg-white rounded-xl shadow-md overflow-hidden"
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900">{faq.question}</h3>
                    {expandedFaq === index ? (
                      <ChevronUp className="text-[#C0A062] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="text-[#C0A062] flex-shrink-0" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="px-6 pb-4">
                      <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-8">Henüz soru bulunmamaktadır.</p>
            )}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-20 bg-white text-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Calendar className="w-16 h-16 mx-auto mb-6 text-[#C0A062]" />
          <h2 className="text-4xl font-bold mb-6 text-gray-900">
            Kampüsümüzü <span className="text-[#C0A062]">Ziyaret Edin</span>
          </h2>
          <p className="text-xl text-gray-700 mb-8">
            Okulumuz hakkında daha fazla bilgi almak ve kampüsümüzü gezmek için randevu alın
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {phoneInfo && phoneInfo.content ? (
              <a
                href={phoneInfo.link || `tel:${phoneInfo.content.replace(/[^\d+]/g, '')}`}
                data-testid="call-cta-btn"
                className="px-8 py-4 bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold rounded-full shadow-xl transition-all"
              >
                Hemen Arayın: {phoneInfo.content}
              </a>
            ) : (
              <a
                href="tel:+903121234567"
                data-testid="call-cta-btn"
                className="px-8 py-4 bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold rounded-full shadow-xl transition-all"
              >
                Hemen Arayın: 0312 123 45 67
              </a>
            )}
            <a
              href="/iletisim"
              data-testid="contact-cta-btn"
              className="px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-full shadow-xl transition-all"
            >
              İletişim Formu
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AdmissionsPage;